import { useState } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import { OutlineCard } from '@morressier/alembic';

import EmailLogHistoryModal from './EmailLogHistoryModal';
import Divider from 'src/components/Divider';

import { type IEmailLog } from '@publishing-engine/domain/types/email-template';
import {
  type IRoleType,
  type IActivityLog
} from '@publishing-engine/domain/models';

import { formatDateAndTime } from 'src/utils/date';
import { generateActivityCopyText } from '../../ActivityTab/activityLog';

interface IEmailLogHistoryTabProps {
  emailLog: IEmailLog;
  name: string;
  authorName?: string;
  email: string;
  roleContext?: IRoleType;
}

export default function EmailLogHistoryTab({
  emailLog,
  name,
  authorName,
  email,
  roleContext
}: IEmailLogHistoryTabProps) {
  const [isEmailViewModelOpen, setIsEmailViewModelOpen] = useState(false);

  // Extract and sanitize body)
  const rawTitle = emailLog.body as string;
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawTitle, 'text/html');
  const body = doc.body.textContent || '';

  const createdAt = formatDateAndTime(emailLog.createdAt);

  const handleEmailViewModal = function () {
    setIsEmailViewModelOpen(true);
  };

  const generateTemplateName = (templateName: string | undefined) => {
    if (!templateName) {
      const logData = {
        date: emailLog.createdAt,
        submissionContextId: emailLog.submissionContextId,
        submissionId: emailLog.submissionId,
        type: emailLog.title,
        reviewerFirstName: emailLog.reviewerFirstName,
        reviewerLastName: emailLog.reviewerLastName
      } as IActivityLog;

      return generateActivityCopyText(logData).title;
    }

    return templateName;
  };

  const getName = function () {
    if (!name.trim() && authorName) {
      return authorName;
    }

    if (
      !name.trim() &&
      emailLog?.template?.event === 'SubmissionCreated' &&
      authorName
    ) {
      return authorName;
    }

    return name;
  };

  return (
    <>
      {isEmailViewModelOpen && (
        <EmailLogHistoryModal
          onClose={() => setIsEmailViewModelOpen(false)}
          emailLog={emailLog}
          fullName={getName()}
          email={email}
          roleContext={roleContext}
        />
      )}

      <OutlineCard padding={4}>
        <Flex direction="column" gap={2}>
          <Flex
            role="group"
            direction="column"
            gap={3}
            p={2}
            onClick={handleEmailViewModal}
            transition="all 0.2s ease-in-out"
            _hover={{
              backgroundColor: 'gray.200',
              cursor: 'pointer'
            }}
          >
            <Box
              color="actionPrimaryContentInverse"
              fontWeight="semibold"
              fontSize="lg"
              transition="all 0.2s ease-in-out"
              _groupHover={{
                textDecoration: 'underline',
                color: 'actionPrimaryContentHoverInverse'
              }}
            >
              {generateTemplateName(emailLog.template.name)}
            </Box>
            <Flex direction="column" gap={1}>
              <Text textStyle="textBody" isTruncated>
                <Box as="span" fontWeight="semibold">
                  Subject:{' '}
                </Box>
                {emailLog.subject}
              </Text>
              <Text textStyle="textBody" noOfLines={2}>
                <Box as="span" fontWeight="semibold">
                  Body Text:{' '}
                </Box>
                {body}
              </Text>
            </Flex>
          </Flex>

          {Divider}

          <Flex direction="row" gap={4} alignItems="center" p={2}>
            <Text textStyle="textBody">
              By {getName()} on {createdAt}
            </Text>
            {/* <Divider
            orientation="vertical"
            height="24px"
            borderColor="gray.400"
          />
          <Text textStyle="textBody">
            Next email to be triggered on Nov 11, 2025
          </Text> */}
          </Flex>
        </Flex>
      </OutlineCard>
    </>
  );
}
